import {
  getMetricColorForMetric,
  yesNoBadgeColorClasses,
  yesNoBadgeShapeClasses,
} from "@/metrics/metric-display"
import { MetadataFieldConfig } from "@/metrics/registry/configuration"
import { MetricRecord } from "@/metrics/runtime"

import { logger } from "@/lib/logger"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import { formatMetricDisplayName } from "@/utils/formatting/format-metric-display-results"
import { formatNumber } from "@/utils/formatting/format-number"

import { getMetadataFieldResult } from "./helpers/metadataFieldResult"

const log = logger("MetadataFieldMetric")

interface MetadataFieldOutputSource {
  result?: Record<string, unknown> | null
  result_json?: Record<string, unknown> | null
}

export class MetadataFieldMetricMetricRecord extends MetricRecord {
  overviewSize = "medium" as const
  unit = "value"
  aggregate_units = "value"

  get hasVersionedConfig() {
    return true
  }

  isMetricOutputCurrentVersion(
    metricOutput: MetadataFieldOutputSource,
  ): boolean {
    const executedResult = getMetadataFieldResult(metricOutput)
    if (!executedResult?.metadata_field_key) {
      return true
    }
    return (
      executedResult.metadata_field_key ===
        (this.metric_metadata?.metadata_field_key || "") &&
      executedResult.metadata_field_type ===
        (this.metric_metadata?.metadata_field_type || "")
    )
  }

  getVersionDiff(
    metricOutput: MetadataFieldOutputSource,
  ): Array<{ field: string; current: string; previous: string }> {
    const diffs: Array<{ field: string; current: string; previous: string }> =
      []
    const executedResult = getMetadataFieldResult(metricOutput)
    if (!executedResult?.metadata_field_key) {
      return diffs
    }
    if (
      executedResult.metadata_field_key !==
      (this.metric_metadata?.metadata_field_key || "")
    ) {
      diffs.push({
        field: "Metadata Field Key",
        current: this.metric_metadata?.metadata_field_key || "",
        previous: executedResult.metadata_field_key,
      })
    }
    if (
      executedResult.metadata_field_type !==
      (this.metric_metadata?.metadata_field_type || "")
    ) {
      diffs.push({
        field: "Metadata Field Type",
        current: this.metric_metadata?.metadata_field_type || "",
        previous: executedResult.metadata_field_type,
      })
    }
    return diffs
  }

  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    const color = getMetricColorForMetric(
      mo.float_value ?? mo.string_value ?? -1,
      this,
    )
    const badgeColor =
      yesNoBadgeColorClasses[color] ?? yesNoBadgeColorClasses.default
    const unitSuffix = this.unit ? ` ${this.unit}` : ""

    if (this.metric_metadata?.metadata_field_type === "BOOLEAN") {
      return (
        <span className={`${yesNoBadgeShapeClasses} ${badgeColor}`}>
          {mo.float_value === 1 ? "True" : "False"}
        </span>
      )
    }

    return (
      <span className={`${yesNoBadgeShapeClasses} ${badgeColor}`}>
        {this.output_type === "string"
          ? formatMetricDisplayName(mo.string_value ?? "N/A")
          : formatNumber(mo.float_value ?? -1)}
        {unitSuffix}
      </span>
    )
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    try {
      const explanation = getMetadataFieldResult(mo)
      if (!explanation) {
        return null
      }

      const renderValue = () => {
        if (mo.float_value === -1) {
          return (
            <span
              className={`${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses.default}`}
            >
              N/A
            </span>
          )
        }
        switch (explanation.metadata_field_type) {
          case "STRING":
            return <p>{explanation.metadata_field_value as string}</p>
          case "FLOAT":
          case "NUMBER":
            return (
              <p>{(explanation.metadata_field_value as number).toFixed(2)}</p>
            )
          case "BOOLEAN":
            return (
              <p>
                {(explanation.metadata_field_value as number) === 1
                  ? "True"
                  : "False"}
              </p>
            )
          default:
            return <p>Unknown type</p>
        }
      }

      return (
        <div className="grid grid-cols-3 gap-4">
          <div>
            <h3 className="text-sm font-semibold text-coval-text-muted">
              Field Type
            </h3>
            <p>{explanation.metadata_field_type}</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-coval-text-muted">
              Field Key
            </h3>
            <p>{explanation.metadata_field_key}</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-coval-text-muted">
              Field Value
            </h3>
            {renderValue()}
          </div>
        </div>
      )
    } catch (error) {
      log.error("Error parsing metadata field explanation", error)
      return (
        <div className="text-red-500">
          Error displaying metadata field information
        </div>
      )
    }
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
  ): React.ReactNode {
    return (
      <MetadataFieldConfig
        metricRecordData={metricRecordData}
        onChange={onChange}
      />
    )
  }

  renderMetadataField(metricRecordData: MetricRecordData): React.ReactNode {
    return (
      <div className="flex flex-col gap-2">
        <div>
          <h3 className="text-sm font-semibold text-coval-text-muted">
            Field Type:{" "}
          </h3>
          <p>{metricRecordData.metric_metadata.metadata_field_type}</p>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-coval-text-muted">
            Field Key:{" "}
          </h3>
          <p>{metricRecordData.metric_metadata.metadata_field_key}</p>
        </div>
      </div>
    )
  }

  renderMetricType(): React.ReactNode {
    return "Metadata Field"
  }
}
